import { useContext } from 'react';
import _ from 'lodash';
import { FlexyDatatableTableContext } from './FlexyDatatableTableContext';

const useSelectItem = <T extends Record<string, unknown> & { id: unknown }>() => {
    const { selectedItems, setSelectedItems, currentData, setCurrentData } = useContext(
        FlexyDatatableTableContext,
    );

    const countSelectedItems = (): number => selectedItems.length;

    const hasSelectedItems = (): boolean => selectedItems.length > 0;

    const isItemSelected = (item: T): boolean =>
        selectedItems.find((dataItem: T) => dataItem.id === item.id) !== undefined;

    const resetRemovedItems = () => {
        const filteredItems = selectedItems.filter((item: T) =>
            currentData.some((dataItem: T) => dataItem.id === item.id),
        );
        if (!_.isEqual(filteredItems, selectedItems)) {
            setSelectedItems(filteredItems);
        }
        return filteredItems;
    };

    const handleSelectItem = (item: T) => {
        const clearItems = resetRemovedItems();

        let tmp = [...clearItems, item];

        if (isItemSelected(item)) {
            tmp = selectedItems.filter((dataItem: T) => dataItem.id !== item.id);
        }

        setSelectedItems(tmp);
    };

    const countUnselectedItems = (): number =>
        currentData.reduce(
            (accumulator, item: T) => accumulator + (!isItemSelected(item) ? 1 : 0),
            0,
        );

    const handleSelectAllItems = () => {
        const clearItems = resetRemovedItems();

        let tmp = [...clearItems];
        const countUnselected = countUnselectedItems();

        currentData.forEach((item: T) => {
            if (countUnselected > 0 && !isItemSelected(item)) {
                tmp.push(item);
            } else if (countUnselected === 0 && isItemSelected(item)) {
                tmp = tmp.filter((dataItem: T) => dataItem.id !== item.id);
            }
        });

        setSelectedItems(tmp);
    };

    return {
        selectedItems,
        countSelectedItems,
        hasSelectedItems,
        isItemSelected,
        handleSelectItem,
        handleSelectAllItems,
        countUnselectedItems,
        setCurrentData,
        resetRemovedItems,
    };
};

export default useSelectItem;
